import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const mappingService = {
  // Get all mappings with optional filters
  getAll: async (filters = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('mappingService: Fetching all mappings with filters:', filters);
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (filters.mapping_type) queryParams.append('mapping_type', filters.mapping_type);
    if (filters.schema_id) queryParams.append('schema_id', filters.schema_id);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.page_size) queryParams.append('page_size', filters.page_size);

    const queryString = queryParams.toString();
    const url = queryString ? `/mappings?${queryString}` : '/mappings';

    const response = await api.get(url);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'getAll');

    // Return the data directly (should be an array of mappings)
    return apiResponse.data || [];
  },

  // Get mapping by ID
  getById: async (id) => {
    const response = await api.get(`/mappings/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'getById');

    // Return the data directly (should be a single mapping object)
    return apiResponse.data;
  },

  // Create new mapping
  create: async (mappingData) => {
    // Create a copy of the mappingData to avoid modifying the original
    const mappingDataToSend = { ...mappingData };

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Creating mapping with data:', mappingData);
      }

      // Ensure all schema_id values are strings if they exist
      if (mappingDataToSend.input_schema_ref?.schema_id) {
        mappingDataToSend.input_schema_ref.schema_id = String(mappingDataToSend.input_schema_ref.schema_id);
      }
      if (mappingDataToSend.output_schema_ref?.schema_id) {
        mappingDataToSend.output_schema_ref.schema_id = String(mappingDataToSend.output_schema_ref.schema_id);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Sending data to API:', JSON.stringify(mappingDataToSend, null, 2));
      }

      const response = await api.post('/mappings', mappingDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'create');

      // Return the data directly (should be the created mapping object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('mappingService: Error creating mapping');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
        console.error('mappingService: Error status:', error.response?.status);
      }

      // Check if this is a schema-related error and provide helpful message
      if (error.response?.status === 400 &&
          error.response?.data?.error?.error?.includes('UUID') &&
          (mappingDataToSend.input_schema_ref || mappingDataToSend.output_schema_ref)) {
        throw new Error(
          'Schema-based mappings are not yet supported by the backend API. ' +
          'Please use flat mappings for now, or contact your administrator to update the backend API to support schema-based mappings.'
        );
      }

      // Rethrow with more detailed error message
      if (error.response?.data) {
        const errorMessage = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
      }
      throw error;
    }
  },

  // Update mapping
  update: async (id, mappingData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Updating mapping with ID:', id, 'and data:', mappingData);
      }

      // Create a copy of the mappingData to avoid modifying the original
      const mappingDataToSend = { ...mappingData };

      const response = await api.put(`/mappings/${id}`, mappingDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'update');

      // Return the data directly (should be the updated mapping object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('mappingService: Error updating mapping');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Delete mapping
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Deleting mapping with ID:', id);
      }

      const response = await api.delete(`/mappings/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'remove');

      // For delete operations, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('mappingService: Error deleting mapping');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Validate mapping with sample data
  validateMapping: async (mappingId, inputData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Validating mapping with ID:', mappingId, 'and input data:', inputData);
      }

      const response = await api.post(`/mappings/${mappingId}/validate`, { input_data: inputData });

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'validateMapping');

      return apiResponse.data;
    } catch (error) {
      console.error('mappingService: Error validating mapping');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Get schema usage for a specific schema
  getSchemaUsage: async (schemaId) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Getting schema usage for schema ID:', schemaId);
      }

      const response = await api.get(`/mappings/schema-usage/${schemaId}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'mappingService', 'getSchemaUsage');

      return apiResponse.data;
    } catch (error) {
      console.error('mappingService: Error getting schema usage');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Get mappings by schema ID
  getMappingsBySchema: async (schemaId) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('mappingService: Getting mappings for schema ID:', schemaId);
      }

      const filters = { schema_id: schemaId };
      return await mappingService.getAll(filters);
    } catch (error) {
      console.error('mappingService: Error getting mappings by schema');

      if (process.env.NODE_ENV === 'development') {
        console.error('mappingService: Error details:', error);
        console.error('mappingService: Error response data:', error.response?.data);
      }

      throw error;
    }
  }
};

export default mappingService;
